import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { GoldButton } from "../components/Buttons";
import { LogOut, Loader2, Users, ClipboardList, Newspaper, RefreshCcw } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import Seo from "../components/Seo";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
    new: "bg-[#C8A45D]/15 text-[#8A6D2F] border-[#C8A45D]/40",
    reviewed: "bg-[#4a7c59]/10 text-[#4a7c59] border-[#4a7c59]/30",
    archived: "bg-[#3D434D]/10 text-[#3D434D] border-[#3D434D]/20",
};

const fmtDate = (iso) => {
    try {
        return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
    } catch {
        return iso || "";
    }
};

function InquiryCard({ inq, staff, onStatus }) {
    const status = inq.status || "new";
    return (
        <div className="bg-white border border-[#E7E4DC] rounded-xl p-5 sm:p-6 shadow-sm" data-testid={`inquiry-card-${inq.id}`}>
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                    <div className="flex items-center gap-3 flex-wrap">
                        <span className={`text-[10px] uppercase tracking-[0.16em] px-2.5 py-1 rounded-full border font-semibold ${STATUS_STYLES[status] || STATUS_STYLES.new}`} data-testid={`inquiry-status-${inq.id}`}>
                            {status}
                        </span>
                        <span className="text-[10px] uppercase tracking-[0.16em] text-[#8A6D2F] bg-[#C8A45D]/10 px-2.5 py-1 rounded-full">{inq.inquiry_type}</span>
                    </div>
                    <h3 className="font-display text-xl text-[#20242B] mt-3">{inq.name}</h3>
                    <p className="text-[#737985] text-sm mt-1">
                        {inq.email}
                        {inq.phone ? ` · ${inq.phone}` : ""}
                    </p>
                </div>
                <p className="text-[#737985] text-xs whitespace-nowrap">{fmtDate(inq.created_at)}</p>
            </div>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 mt-4 text-sm">
                {inq.service && (
                    <div className="flex gap-2"><dt className="text-[#737985]">Service:</dt><dd className="text-[#20242B] font-medium">{inq.service}</dd></div>
                )}
                {inq.tax_years && (
                    <div className="flex gap-2"><dt className="text-[#737985]">Tax year(s):</dt><dd className="text-[#20242B] font-medium">{inq.tax_years}</dd></div>
                )}
                {inq.company && (
                    <div className="flex gap-2"><dt className="text-[#737985]">Company:</dt><dd className="text-[#20242B] font-medium">{inq.company}</dd></div>
                )}
            </dl>
            <p className="text-[#4A4F58] text-sm leading-relaxed mt-4 whitespace-pre-wrap">{inq.message}</p>
            {staff && (
                <div className="mt-5 pt-4 border-t border-[#E7E4DC]">
                    <label className="text-[10px] uppercase tracking-[0.16em] text-[#737985] block mb-2">Update status</label>
                    <select
                        value={status}
                        onChange={(e) => onStatus(inq.id, e.target.value)}
                        className="h-10 border border-[#E7E4DC] rounded-lg px-3 text-sm text-[#20242B] bg-white focus:outline-none focus:border-[#C8A45D]"
                        data-testid={`inquiry-status-select-${inq.id}`}
                    >
                        <option value="new">New</option>
                        <option value="reviewed">Reviewed</option>
                        <option value="archived">Archived</option>
                    </select>
                </div>
            )}
        </div>
    );
}

function TeamAdmin() {
    const [users, setUsers] = useState(null);
    const [form, setForm] = useState({ name: "", email: "", password: "", role: "staff" });
    const [error, setError] = useState("");

    const load = () => {
        axios.get(`${API}/api/admin/users`, { withCredentials: true })
            .then(({ data }) => setUsers(data.users))
            .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail)));
    };
    useEffect(load, []);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        try {
            await axios.post(`${API}/api/admin/users`, form, { withCredentials: true });
            toast.success(`${form.name} can now sign in.`);
            setForm({ name: "", email: "", password: "", role: "staff" });
            load();
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const inputCls = "w-full h-11 bg-white border border-[#E7E4DC] rounded-lg px-3 text-sm text-[#20242B] placeholder-[#9aa1ab] focus:outline-none focus:border-[#C8A45D] transition-colors duration-200";

    return (
        <div className="bg-white border border-[#E7E4DC] rounded-xl p-5 sm:p-6 shadow-sm mt-10" data-testid="team-section">
            <h2 className="font-display text-2xl text-[#20242B] flex items-center gap-3">
                <Users className="h-5 w-5 text-[#8A6D2F]" /> Team Accounts
            </h2>
            <ul className="mt-4 divide-y divide-[#E7E4DC]">
                {(users || []).map((u) => (
                    <li key={u.id} className="py-3 flex items-center justify-between gap-3 text-sm" data-testid={`team-user-${u.email}`}>
                        <span className="text-[#20242B]">{u.name} <span className="text-[#737985]">· {u.email}</span></span>
                        <span className="text-[10px] uppercase tracking-[0.14em] text-[#8A6D2F] bg-[#C8A45D]/10 px-2.5 py-1 rounded-full">{u.role}</span>
                    </li>
                ))}
                {users && users.length === 0 && <li className="py-3 text-sm text-[#737985]">No accounts yet.</li>}
            </ul>
            <form onSubmit={submit} className="mt-5 pt-5 border-t border-[#E7E4DC] grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="staff-create-form">
                <input required maxLength={120} value={form.name} onChange={set("name")} placeholder="Team member name" className={inputCls} data-testid="staff-name" />
                <input type="email" required value={form.email} onChange={set("email")} placeholder="Team member email" className={inputCls} data-testid="staff-email" />
                <input type="password" required minLength={8} value={form.password} onChange={set("password")} placeholder="Temporary password (min 8 chars)" className={inputCls} data-testid="staff-password" />
                <select value={form.role} onChange={set("role")} className={inputCls} data-testid="staff-role">
                    <option value="staff">Staff — sees all inquiries</option>
                    <option value="admin">Admin — also manages team</option>
                </select>
                {error && <p className="text-[#c0392b] text-sm sm:col-span-2" data-testid="staff-error">{error}</p>}
                <div className="sm:col-span-2">
                    <GoldButton type="submit" className="h-11 px-6" data-testid="staff-create-button">Add Team Account</GoldButton>
                </div>
            </form>
        </div>
    );
}

const NEWS_CATEGORY_LABELS = {
    all: "All IRS newsroom items",
    deadlines: "Filing deadlines & reminders",
    tax_tips: "Tax tips & planning",
    refunds_credits: "Refunds, credits & payments",
    security_alerts: "Scams & security alerts",
};
const NEWS_CATEGORY_KEYS = ["all", "deadlines", "tax_tips", "refunds_credits", "security_alerts"];

function NewsAdmin({ isAdmin }) {
    const [settings, setSettings] = useState(null);
    const [draft, setDraft] = useState(null);
    const [articles, setArticles] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = () => {
        axios
            .get(`${API}/api/admin/news`, { withCredentials: true })
            .then(({ data }) => {
                setSettings(data.settings);
                setDraft({
                    enabled: !!data.settings.enabled,
                    categories: data.settings.categories || [],
                    max_articles: data.settings.max_articles || 12,
                });
                setArticles(data.articles);
            })
            .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load IRS news settings."));
    };
    useEffect(load, []);

    const toggleCategory = (key) => {
        setDraft((d) => {
            if (!d.categories.includes(key)) {
                return { ...d, categories: key === "all" ? ["all"] : [...d.categories.filter((c) => c !== "all"), key] };
            }
            const rest = d.categories.filter((c) => c !== key);
            if (rest.length === 0) {
                toast.error("Select at least one category.");
                return d;
            }
            return { ...d, categories: rest };
        });
    };

    const save = async () => {
        if (!draft.categories.length) {
            toast.error("Select at least one category.");
            return;
        }
        setBusy(true);
        try {
            const payload = {
                enabled: draft.enabled,
                categories: draft.categories,
                max_articles: Math.max(1, Math.min(50, parseInt(draft.max_articles, 10) || 12)),
            };
            const { data } = await axios.put(`${API}/api/admin/news/settings`, payload, { withCredentials: true });
            setSettings(data.settings);
            setDraft({ enabled: data.settings.enabled, categories: data.settings.categories, max_articles: data.settings.max_articles });
            toast.success("IRS news settings saved.");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save settings.");
        }
        setBusy(false);
    };

    const refresh = async () => {
        try {
            await axios.post(`${API}/api/admin/news/refresh`, {}, { withCredentials: true });
            toast.success("Sync from IRS.gov started — the list updates in a few seconds.");
            setTimeout(load, 6000);
        } catch {
            toast.error("Could not start the sync. Please try again.");
        }
    };

    const toggleHidden = async (a) => {
        try {
            await axios.patch(`${API}/api/admin/news/${a.id}/hidden`, { hidden: !a.hidden }, { withCredentials: true });
            setArticles((list) => list.map((x) => (x.id === a.id ? { ...x, hidden: !a.hidden } : x)));
            toast.success(a.hidden ? "Article is visible on the site again." : "Article hidden from the site (the original stays saved).");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update the article.");
        }
    };

    const fmtSynced = (iso) => {
        try {
            return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
        } catch {
            return "—";
        }
    };

    const inputCls = "w-20 h-10 bg-white border border-[#E7E4DC] rounded-lg px-3 text-sm text-[#20242B] focus:outline-none focus:border-[#C8A45D]";

    return (
        <div className="bg-white border border-[#E7E4DC] rounded-xl p-5 sm:p-6 shadow-sm mt-10" data-testid="news-admin-section">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <h2 className="font-display text-2xl text-[#20242B] flex items-center gap-3">
                    <Newspaper className="h-5 w-5 text-[#8A6D2F]" /> IRS News Settings
                </h2>
                <button
                    onClick={refresh}
                    data-testid="news-refresh-button"
                    className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-lg border border-[#E7E4DC] text-[#3D434D] text-xs uppercase tracking-[0.1em] hover:border-[#C8A45D] hover:text-[#8A6D2F] transition-colors duration-200"
                >
                    <RefreshCcw className="h-3.5 w-3.5" /> Sync From IRS Now
                </button>
            </div>
            {settings && (
                <p className="text-[#737985] text-xs mt-2" data-testid="news-sync-status">
                    Source: official IRS newsroom (IRS.gov) · Last check: {fmtSynced(settings.last_fetch_at)} · Status:{" "}
                    {settings.last_fetch_status || "—"} · Checks run automatically every 4 hours.
                </p>
            )}

            {isAdmin && draft ? (
                <div className="mt-5 pt-5 border-t border-[#E7E4DC] grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                    <label className="flex items-center gap-3 text-sm text-[#20242B]" data-testid="news-enabled-row">
                        <Switch checked={draft.enabled} onCheckedChange={(v) => setDraft((d) => ({ ...d, enabled: v }))} data-testid="news-enabled-switch" />
                        Show "IRS News &amp; Tax Updates" on the website
                    </label>
                    <label className="flex items-center gap-3 text-sm text-[#20242B] sm:justify-self-end">
                        Articles shown:
                        <input
                            type="number"
                            min={1}
                            max={50}
                            value={draft.max_articles}
                            onChange={(e) => setDraft((d) => ({ ...d, max_articles: e.target.value }))}
                            className={inputCls}
                            data-testid="news-max-articles"
                        />
                    </label>
                    <div className="sm:col-span-2">
                        <p className="text-[10px] uppercase tracking-[0.16em] text-[#737985] mb-2">Categories shown (an article must match at least one)</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {NEWS_CATEGORY_KEYS.map((key) => (
                                <label
                                    key={key}
                                    className="flex items-center gap-3 text-sm text-[#20242B] border border-[#E7E4DC] rounded-lg px-3 py-2.5 cursor-pointer hover:border-[#C8A45D]/60 transition-colors duration-200"
                                    data-testid={`news-cat-${key}`}
                                >
                                    <input type="checkbox" checked={draft.categories.includes(key)} onChange={() => toggleCategory(key)} className="accent-[#C8A45D] h-4 w-4" />
                                    {NEWS_CATEGORY_LABELS[key]}
                                </label>
                            ))}
                        </div>
                    </div>
                    <div className="sm:col-span-2">
                        <GoldButton onClick={save} disabled={busy} className="h-11 px-6" data-testid="news-save-settings">
                            {busy ? "Saving…" : "Save News Settings"}
                        </GoldButton>
                    </div>
                </div>
            ) : null}
            {isAdmin && !draft && <p className="mt-5 text-sm text-[#737985]">Loading settings…</p>}
            {!isAdmin && (
                <p className="mt-5 pt-5 border-t border-[#E7E4DC] text-sm text-[#737985]">
                    You can hide articles below. Only admins can change news settings.
                </p>
            )}

            <div className="mt-6 pt-5 border-t border-[#E7E4DC]">
                <p className="text-[10px] uppercase tracking-[0.16em] text-[#737985] mb-3">
                    Articles synced from IRS.gov — hide anything you don't want shown (the original stays saved)
                </p>
                {!articles && <p className="text-sm text-[#737985]">Loading articles…</p>}
                {articles && articles.length === 0 && (
                    <p className="text-sm text-[#737985]" data-testid="news-admin-empty">
                        No articles synced yet. Click "Sync From IRS Now".
                    </p>
                )}
                <ul className="space-y-2">
                    {(articles || []).map((a) => (
                        <li
                            key={a.id}
                            className={`flex flex-col sm:flex-row sm:items-center justify-between gap-2 border border-[#E7E4DC] rounded-lg px-4 py-3 text-sm ${a.hidden ? "opacity-50" : ""}`}
                            data-testid={`news-article-row-${a.id}`}
                        >
                            <span>
                                <span className="text-[#20242B] font-medium">{a.title}</span>
                                <span className="block text-[#737985] text-xs mt-0.5">
                                    {a.published_display || ""}
                                    {a.hidden ? " · Hidden from website" : ""}
                                </span>
                            </span>
                            <button
                                onClick={() => toggleHidden(a)}
                                data-testid={`news-hide-button-${a.id}`}
                                className="shrink-0 self-start sm:self-center h-9 px-4 rounded-lg border border-[#E7E4DC] text-xs uppercase tracking-[0.1em] text-[#3D434D] hover:border-[#C8A45D] hover:text-[#8A6D2F] transition-colors duration-200"
                            >
                                {a.hidden ? "Unhide" : "Hide"}
                            </button>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}

export default function Dashboard() {
    const { user, logout } = useAuth();
    const [inquiries, setInquiries] = useState(null);
    const [error, setError] = useState("");
    const isStaff = user && (user.role === "admin" || user.role === "staff");

    const load = () => {
        const url = isStaff ? `${API}/api/admin/inquiries` : `${API}/api/my/inquiries`;
        axios.get(url, { withCredentials: true })
            .then(({ data }) => setInquiries(data.inquiries))
            .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
    };
    useEffect(load, [isStaff]);

    const updateStatus = async (id, status) => {
        try {
            await axios.patch(`${API}/api/admin/inquiries/${id}/status`, { status }, { withCredentials: true });
            setInquiries((list) => list.map((i) => (i.id === id ? { ...i, status } : i)));
            toast.success("Status updated.");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update status.");
        }
    };

    return (
        <section className="min-h-[80vh] bg-[#FAFAF7] pt-32 pb-24">
            <Seo title="My Dashboard | Freedom Tax Filer" description="Your Freedom Tax Filer inquiries dashboard." path="/dashboard" noindex />
            <div className="mx-auto max-w-5xl px-5 sm:px-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <p className="text-[#8A6D2F] text-[12px] uppercase tracking-[0.2em]">
                            {isStaff ? "Inquiries Dashboard" : "My Inquiries"}
                        </p>
                        <h1 className="font-display text-3xl sm:text-4xl text-[#20242B] mt-3" data-testid="dashboard-title">
                            Hello, {user.name || user.email.split("@")[0]}.
                        </h1>
                        <p className="text-[#737985] text-sm mt-2" data-testid="dashboard-role">
                            {isStaff
                                ? "Every inquiry from the website, newest first."
                                : "Inquiries you submitted with this email address."}
                        </p>
                    </div>
                    <button
                        onClick={logout}
                        className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-lg border border-[#E7E4DC] text-[#3D434D] text-sm hover:border-[#C8A45D] hover:text-[#8A6D2F] transition-colors duration-200"
                        data-testid="signout-button"
                    >
                        <LogOut className="h-4 w-4" /> Sign Out
                    </button>
                </div>

                {error && <p className="mt-8 text-[#c0392b] text-sm" data-testid="dashboard-error">{error}</p>}

                {!inquiries && !error && (
                    <div className="mt-10 flex items-center gap-3 text-[#737985]" data-testid="dashboard-loading">
                        <Loader2 className="h-5 w-5 animate-spin" /> Loading inquiries…
                    </div>
                )}

                {inquiries && inquiries.length === 0 && (
                    <div className="mt-10 bg-white border border-[#E7E4DC] rounded-xl p-10 text-center" data-testid="dashboard-empty">
                        <ClipboardList className="h-8 w-8 text-[#C8A45D] mx-auto" />
                        <p className="text-[#3D434D] mt-4">
                            {isStaff ? "No inquiries yet. New form submissions will appear here." : "You have not submitted an inquiry yet."}
                        </p>
                    </div>
                )}

                <div className="mt-8 space-y-5">
                    {(inquiries || []).map((inq) => (
                        <InquiryCard key={inq.id} inq={inq} staff={isStaff} onStatus={updateStatus} />
                    ))}
                </div>

                {isStaff && <NewsAdmin isAdmin={user.role === "admin"} />}

                {isStaff && user.role === "admin" && <TeamAdmin />}
            </div>
        </section>
    );
}
